import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.1.3/js/sparta-global-functions-utility";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.1.1/js/sparta-store-utility";
import spartaStyleUtility from "utilities/global/sparta-style-utility/3.2.3/js/sparta-style-utility";
import handlebarsRuntime from "utilities/vendor/handlebars-runtime/4.7.6/js/handlebars.runtime";
import hb from "utilities/vendor/handlebars-runtime/4.7.6/js/handlebars.runtime";
import _ from "utilities/vendor/lodash/4.13.1/js/lodash.custom.min";
import Module from "utilities/global/sparta-module-utility/6.2.6/js/sparta-module-utility";
import spartaFootnoteUtility from "utilities/global/sparta-footnote-utility/4.4.1/js/sparta-footnote-utility";
import spartaTemplates from "modules/global/global-footer-module/12.1.0/js/templates";
import "css!modules/global/global-footer-module/12.1.0/css/global-footer-module.scss.css";
/* global Module bactm */

const selectors = {
  legalToggle: '.legal-section__header',
  legalSection: '.legal-section__panel',
  legalContent: '.legal-section__panel .row',
  footnotesSection: '.legal-section__footnotes',
  powerFooter: '.power-footer',
  powerFooterToggle: '.power-footer__toggle',
  powerFooterLinks: '.power-footer__links',
  bottomLinks: 'nav.footer__bottom-links li',
  feedbackLink: '#footer_bofa_feedback',
  footer: 'footer',
  nav: 'nav',
  ul: 'ul',
};

const cssClasses = {
  mdaHide: 'mda-hide',
  hide: 'hide',
  collapsed: 'collapsed',
};

/** Class providing interactive features to global-footer-module */
export default class GlobalFooterModuleJS extends Module {
  /**
   * Add event listeners and call initialization functions
   * @returns {undefined}
   */
  init() {
    const moduleEl = this.settings.target;
    const legalToggleElem = moduleEl.querySelector(selectors.legalToggle);
    const powerFooterSection = moduleEl.querySelector(selectors.powerFooter);
    const legalSection = moduleEl.querySelector(selectors.legalSection);
    const footer = moduleEl.querySelector(selectors.footer);
    const feedbackLink = moduleEl.querySelector(selectors.feedbackLink);

    // Listen for click events
    if (legalToggleElem) {
      legalToggleElem.addEventListener('click', this.legalHeaderClick(legalSection).bind(this));
    }

    if (powerFooterSection) {
      powerFooterSection.addEventListener('click', this.powerFooterHeaderClick.bind(this));
    }

    if (feedbackLink) {
      feedbackLink.addEventListener('click', GlobalFooterModuleJS.launchFeedback);
    }

    // Let animations end before acting
    moduleEl.addEventListener('transitionend', GlobalFooterModuleJS.hideInvisibleContent, false);

    // subscribe to breakpoint change events (via matchMedia)
    const bpResetFunction = this.breakpointReset({ legalSection, powerFooterSection, footer });
    this.Events.sub('BREAKPOINT_CHANGE.spartaMediaQueryUtility', bpResetFunction.bind(this), false);

    this.mdaHideNotSecureLinks();

    this.initLegalSection(legalSection);

    this.initPowerFooter(powerFooterSection);

    this.initMutationObserver();

    const resetFootnoteHeight = this.resetFootnoteHeight.bind(this);
    window.addEventListener('resize', _.debounce(resetFootnoteHeight, 250));

    this.Events.sub(spartaFootnoteUtility.EVENTS.FOOTNOTE_CLICKED,
      this.openLegalSectionOnFootnoteClick.bind(this, legalSection, legalToggleElem));
  }

  /**
   * Initialize the mutation observer
   */
  initMutationObserver() {
    const footnotesSection = this.settings.target.querySelector(selectors.footnotesSection);

    if (!footnotesSection) return;

    try {
      const observer = new MutationObserver(this.resetFootnoteHeight.bind(this));

      requestAnimationFrame(() => {
        observer.observe(footnotesSection, {
          attributes: true,
          childList: true,
          subtree: true, //ADA FIX SPARTADEV-4525
        });
      });
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Handles reset footnote height for animation
   * @returns {void}
   */
  resetFootnoteHeight() {
    this.makeAutoHeightExplicit({
      targetSelector: selectors.legalSection,
      referenceSelector: selectors.legalContent,
    });
  }

  /**
   * Add the "mda-hide" class to any non-secure links in the bottom nav
   * @returns {void}
   */
  mdaHideNotSecureLinks() {
    if (document.documentElement.classList.contains('mda')) {
      const notSecureLinks = [...this.settings.target.querySelectorAll(`${selectors.bottomLinks} a[href^="http://"]`)];
      if (notSecureLinks.length > 0) {
        requestAnimationFrame(() => {
          notSecureLinks.forEach((link) => {
            link.parentNode.classList.add(cssClasses.mdaHide);
          });
        });
      }
    }
  }

  /**
   * Return a function that will reset footer sections when breakpoint changes
   *
   * @param {object} elements - Collection of elements containing legal & power footer sections
   * @param {HTMLElement} elements.legalSection - Legal section DOM node
   * @param {HTMLElement} elements.powerFooterSection - Powerfooter section DOM node
   * @returns {void}
   */
  breakpointReset({ legalSection, powerFooterSection, footer }) {
    return () => {
      this.resetLegalSection(legalSection, footer);
      this.initPowerFooter(powerFooterSection);
    };
  }

  /**
   * Possibly expand the legal section based on module configuration and current breakpoint
   * @param {HTMLElement} legalSection - Legal section DOM node
   * @returns {void}
   */
  initLegalSection(legalSection) {
    if (!legalSection) return;

    const currentMQ = spartaStoreUtility.getData('currentMQ');
    const legalExpandOnLoad = this.params.legalExpandOnLoad || {};
    const hasProp = Object.prototype.hasOwnProperty;
    const unexpectedViewport = !hasProp.call(legalExpandOnLoad, currentMQ.viewport);

    if (unexpectedViewport || legalExpandOnLoad[currentMQ.viewport]) {
      this.toggleLegalSection(legalSection);
    }
  }

  /**
   * Reset & re-initialize the legal section. This is called on breakpoint change.
   * @param {HTMLElement} legalSection - Legal section DOM node
   * @param {HTMLElement} footer - footer DOM node
   * @returns {void}
   */
  resetLegalSection(legalSection, footer) {
    if (footer) {
      footer.classList.add(cssClasses.collapsed);
    }

    if (legalSection) {
      legalSection.classList.remove(cssClasses.hide);
      legalSection.style.height = 'auto'; // eslint-disable-line no-param-reassign

      this.initLegalSection(legalSection);
    }
  }

  /**
   * Partial application of `toggleLegalSection` method that can be used as an event handler.
   * @param  {HTMLElement} legalSection DOM Element for collapsible legal section
   * @returns {void}
   */
  legalHeaderClick(legalSection) {
    return event => this.toggleLegalSection(legalSection, event);
  }

  /**
   * Toggle visibility of the legal section
   *
   * @param  {HTMLElement} legalSection DOM Element for collapsible legal section
   * @param  {object} event - event object
   * @returns {undefined}
   */
  toggleLegalSection(legalSection, event) {
    if (event) {
      event.preventDefault();
    }

    const footer = this.settings.target.querySelector(selectors.footer);

    // remove the hide class before setting explicit height for CSS transition
    // if we are hiding, this will be added at end of animation
    legalSection.classList.remove(cssClasses.hide);

    requestAnimationFrame(() => {
      this.makeAutoHeightExplicit({
        targetSelector: selectors.legalSection,
        referenceSelector: selectors.legalContent,
      });

      // toggle the state class
      // footer.classList.toggle(cssClasses.collapsed);
      if (footer.classList.contains(cssClasses.collapsed)) {
        this.openLegalSection();
      } else {
        this.closeLegalSection();
      }

      // sync the aria attributes after toggling
      GlobalFooterModuleJS.setAriaExpanded({
        referenceElem: footer,
        targetElem: footer.querySelector('a'),
      });
    });
  }

  /**
   * Opens the legal section
   */
  openLegalSection() {
    const footer = this.settings.target.querySelector(selectors.footer);
    footer.classList.remove(cssClasses.collapsed);
  }

  /**
   * Closes the legal section
   */
  closeLegalSection() {
    const footer = this.settings.target.querySelector(selectors.footer);
    footer.classList.add(cssClasses.collapsed);
  }

  /**
   * Opens the legal section when a footnote is clicked
   * @param {HTMLElement} legalSection - DOM Element for collapsible legal section
   * @param {HTMLElement} legalToggleElem - DOM Element containing the toggle link
   * @returns {undefined}
   */
  openLegalSectionOnFootnoteClick(legalSection, legalToggleElem) {
    if (legalSection.classList.contains(cssClasses.hide)) {
      // remove the hide class before setting explicit height for CSS transition
      legalSection.classList.remove(cssClasses.hide);

      requestAnimationFrame(() => {
        this.makeAutoHeightExplicit({
          targetSelector: selectors.legalSection,
          referenceSelector: selectors.legalContent,
        });
        this.openLegalSection();
        const legalToggleLink = legalToggleElem.querySelector('a');
        legalToggleLink.setAttribute('aria-expanded', true);
      });
    }
  }


  /* eslint-disable class-methods-use-this */ // this method doesn't make sense as a static
  /**
   * Remove 'collapsed' class from `nav` and 'hide' class from links for medium-up
   * @see https://jsperf.com/adding-classes-to-multiple-elements/
   *
   * @param  {HTMLElement} powerFooterSection DOM Element for power footer section
   * @returns {undefined}
   */
  initPowerFooter(powerFooterSection) {
    if (!powerFooterSection) return;

    const currentMQ = spartaStoreUtility.getData('currentMQ');
    const navs = powerFooterSection.querySelectorAll(selectors.nav);
    const links = powerFooterSection.querySelectorAll(selectors.powerFooterLinks);

    requestAnimationFrame(() => {
      if (currentMQ.mediaQueries.medium) {
        [...navs].forEach((nav) => {
          nav.classList.remove(cssClasses.collapsed);
        });

        [...links].forEach((linkList) => {
          linkList.classList.remove(cssClasses.hide);
        });
      } else {
        [...navs].forEach((nav) => {
          nav.classList.add(cssClasses.collapsed);
        });

        [...links].forEach((linkList) => {
          linkList.classList.add(cssClasses.hide);
        });
      }
    });
  }
  /* eslint-enable class-methods-use-this */

  /**
   * Event handler for toggling power footer link sections
   *
   * @param  {object} event - event object
   * @returns {undefined}
   */
  powerFooterHeaderClick(event) {
    const isHeaderLink = event.target.matches(selectors.powerFooterToggle);
    if (!isHeaderLink) return; // bail early if click was not on a toggle control

    event.preventDefault();

    this.togglePowerFooterSection(event.target);

    // set appropriate aria-expanded attributes
    GlobalFooterModuleJS.setAriaExpanded({
      referenceElem: event.target.closest(selectors.nav),
      targetElem: event.target,
    });
  }

  /**
   * Toggle visibility of the power footer link sections
   *
   * @param {HTMLElement} targetEl - a.toggle-links inside power footer column header
   * @returns {undefined}
   */
  togglePowerFooterSection(targetEl) {
    const nav = targetEl.closest(selectors.nav);

    // remove the hide class before setting explicit height for CSS transition
    // if we are hiding, this will be added at end of animation
    nav.querySelector(selectors.powerFooterLinks).classList.remove(cssClasses.hide);

    this.makeAutoHeightExplicit({
      targetSelector: `#${nav.id} ${selectors.powerFooterLinks}`,
      referenceSelector: `#${nav.id} ${selectors.ul}`,
    });

    nav.classList.toggle(cssClasses.collapsed);
  }

  /**
   * Set the CSS height attribute to a fixed value based on the height of another
   * element on the page. This is helpful for animating (via CSS transform) elements
   * that are laid out with `height: auto`.
   * @param  {object} selectors - CSS selectors to operate
   * @param  {string} selectors.targetSelector - Selector for element to set height for
   * @param  {string} selectors.referenceSelector - Selector for element to read height from
   * @returns {void}
   */
  makeAutoHeightExplicit({ targetSelector, referenceSelector }) {
    const targetSection = this.settings.target.querySelector(targetSelector);
    const contentSection = this.settings.target.querySelector(referenceSelector);
    const contentHeight = contentSection ? contentSection.getBoundingClientRect().height : 0;

    if (targetSection) {
      targetSection.style.height = `${contentHeight}px`;
    }
  }

  /**
   * Launch feedback modal
   * @param  {MouseEvent} event - Click event
   * @returns {void}
   */
  static launchFeedback(event) {
    event.preventDefault();

    try {
      bactm.plugins.feedback.launch();
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Event callback to make hidden content inaccessible to screen readers and keyboard navication
   * @param  {object} event - `transitionend` event called when sections are collapsed/expanded
   * @returns {void}
   */
  static hideInvisibleContent(event) {
    const elDimensions = event.target.getBoundingClientRect();
    const hiddenEl = elDimensions.height === 0 || elDimensions.width === 0;

    if (hiddenEl) {
      event.target.classList.add(cssClasses.hide);
    }
  }

  /**
   * @param  {object} Elements - Collection of reference and taget `Elements`.
   * Calls `GlobalFooterModuleJS.syncAttrClass` with some properties already set
   *
   * @param  {HTMLElement} Elements.referenceElem - Element to check for 'collapsed' class on
   * @param  {HTMLElement} Elements.targetElem - Element to set the 'aria-expanded' attribute on
   * @returns {void}
   */
  static setAriaExpanded({ referenceElem, targetElem }) {
    GlobalFooterModuleJS.syncAttrClass({
      referenceElem,
      targetElem,
      attr: 'aria-expanded',
      className: cssClasses.collapsed,
      invert: true,
    });
  }

  /**
   * Check if `className` is present on `referenceElem` and set `attr` to `true`
   * if present or `false` if absent
   * @param {object} args - Configuration object
   * @param {HTMLElement} args.referenceElem - Element to check for 'className' class on
   * @param {HTMLElement} args.targetElem - Element to set the attribute on
   * @param {string} args.attr - Attribute to change
   * @param {string} args.className - Name of the class check for
   * @param {boolean} args.invert - `attr` should be false if `className` is present
   *
   * @returns {undefined}
   */
  static syncAttrClass({
    referenceElem,
    targetElem,
    attr,
    className,
    invert,
  }) {
    let attrVal = referenceElem.classList.contains(className);

    if (invert) {
      attrVal = !attrVal;
    }

    targetElem.setAttribute(attr, attrVal);
  }
}
